import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import { t, tc } from '@owl-nest/localize'
import * as date from '@owl-nest/date'
import * as api from '@owl-nest/api-client/latest'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import * as model from '@owl-nest/models'

import * as styles from '../style'

import * as modalAction from '../../../../actions/modal'
import * as projectModel from '../../../models/project'
import { formatBacker } from '../utils/formatBacker'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../utils/trackerEnums'
import * as URLs from '../../../utils/URLs'

type BackerPropTypes = {
  supporter: api.Supporter
  project: projectModel.Project
  variant?: string
  source: TRACKING_SOURCE
}

export function Backer({ supporter, project, variant, source }: BackerPropTypes): React.ReactElement<BackerPropTypes> {
  const { dateOrder } = formatBacker(supporter, project)

  const isOwnerComment = supporter.id === project.owner.id

  const hasComment = !isOwnerComment && supporter.latest_project_comment && supporter.latest_project_comment?.is_public
  const tag = model.user.tag(supporter, supporter.role)

  return (
    <styles.BackerContainer $variant={variant}>
      <plume.UserLabel
        avatar={model.user.avatar(supporter, '55x55')}
        avatarSize="medium"
        isPublic={supporter.is_public}
        href={supporter.absolute_url}
      />

      <BackerInfo $variant={variant}>
        <CopyWrapper hasComment={hasComment}>
          <div>
            <Copy>
              <plume.UserLabel
                isPublic={supporter.is_public}
                href={supporter.absolute_url}
                name={
                  <plume.styles.heading.XXXS asDesktop="XS" as="span">
                    {model.user.fullName(supporter)}
                  </plume.styles.heading.XXXS>
                }
                tag={tag}
              />
            </Copy>
            <plume.styles.copy.M.light as="span">
              {project.type === api.ProjectType.Donation ? (
                <DonationBasedContent project={project} supporter={supporter} />
              ) : (
                <RewardBasedContent project={project} source={source} supporter={supporter} />
              )}
            </plume.styles.copy.M.light>
          </div>
          {/* Comments */}
          {hasComment && <CommentWrapper>{supporter.latest_project_comment?.comment}</CommentWrapper>}
        </CopyWrapper>
        {/* Last contribution date */}
        {dateOrder && <DateInfo>{date.formatDistanceToNow(dateOrder, { includeSeconds: true })}</DateInfo>}
      </BackerInfo>
    </styles.BackerContainer>
  )
}
export function DonationBasedContent({ supporter, project }: Omit<BackerPropTypes, 'source'>) {
  const { supporterLatestOrder } = formatBacker(supporter, project)

  return (
    <>
      {supporterLatestOrder?.type === 'monthly'
        ? tc('made a [Link: monthly donation of %(price)s]', {
            Link: (
              <plume.Link
                tinted
                href={
                  model.project.isOrdersEnabled<projectModel.ExtraFields>(project)
                    ? URLs.getCheckoutURL(project.id)
                    : ''
                }
              />
            ),
            price: plume.accounting.localeMoney(supporterLatestOrder?.order_total, {
              currency: project.currency,
              lang: project.lang,
            }),
          })
        : tc('made a [Link: donation of %(price)s]', {
            Link: (
              <plume.Link
                tinted
                href={
                  model.project.isOrdersEnabled<projectModel.ExtraFields>(project)
                    ? URLs.getCheckoutURL(project.id)
                    : ''
                }
              />
            ),
            price: plume.accounting.localeMoney(supporterLatestOrder?.order_total, {
              currency: project.currency,
              lang: project.lang,
            }),
          })}
    </>
  )
}

export function RewardBasedContent({ supporter, project, source }: BackerPropTypes) {
  const dispatch = Redux.useDispatch()

  const tracking = useTracking()

  const postCampaignLink = project?.post_campaign_link

  const { isLegacy, isHidden, hasTip, firstReward, supporterLatestOrder } = formatBacker(supporter, project)

  const rewardTitle = firstReward ? model.reward.title(firstReward) : undefined

  function rewardContent() {
    if (firstReward) {
      if (isHidden) {
        return t('supports this project')
      }

      if (isLegacy) {
        if (hasTip && supporterLatestOrder?.tip) {
          return tc('supports this project with [price]', {
            price: (
              <plume.LocaleMoneyDisplayer
                amount={supporterLatestOrder?.tip + firstReward.price}
                currency={project.currency}
                localeFormat={project.lang}
              />
            ),
          })
        }
        return tc('supports this project with [price]', {
          price: (
            <plume.LocaleMoneyDisplayer
              amount={firstReward.price}
              currency={project.currency}
              localeFormat={project.lang}
            />
          ),
        })
      }

      if (supporterLatestOrder?.items && supporterLatestOrder?.items.length > 1) {
        return t('chose %(nb)s rewards', { nb: supporterLatestOrder.items.length })
      }

      if (rewardTitle) {
        return tc('chose the reward [link: %(reward)s]', {
          link: (
            <plume.Link
              tinted
              onClick={() => {
                if (model.project.isFinished<projectModel.ExtraFields>(project) && postCampaignLink !== undefined) {
                  dispatch(modalAction.toggleModalPostCampaign(true))
                } else {
                  tracking.track({
                    event: EVENT_TYPE.GENERIC,
                    eventCategory: CATEGORY.PROJECT_PAGE,
                    eventAction: TRACKING_ACTION.DISPLAY_REWARD,
                    eventLabel: source,
                  })
                  dispatch(modalAction.toggleModalReward(true, { id: firstReward.id.toString() }))
                }
              }}
            />
          ),
          reward: rewardTitle,
        })
      }
    }

    if (!hasTip) {
      return t('supported the project with a donation')
    }

    return <></>
  }

  return (
    <>
      {rewardContent()}
      {/* Free contribution */}
      {hasTip &&
        !isLegacy &&
        !isHidden &&
        supporterLatestOrder &&
        firstReward &&
        t(' and supported the project with a donation')}
      {hasTip &&
        !isLegacy &&
        !isHidden &&
        supporterLatestOrder &&
        !firstReward &&
        t('supported the project with a donation')}
    </>
  )
}

const BackerInfo = styled(styles.BackerInfo)`
  ${plume.styles.copy.M.light} {
    margin-bottom: 0;
  }

  ${plume.styles.copy.M.light}, ${plume.Link} {
    display: inline;
  }
`

const CopyWrapper = styled.div<{ hasComment?: boolean }>`
  margin-right: 5px;
  display: flex;

  ${({ hasComment }) => {
    return hasComment
      ? css`
          flex-wrap: wrap;
          flex-direction: column;
        `
      : css`
          align-items: center;
        `
  }}
`

const Copy = styled.p`
  margin: 0 0 5px 0;
  display: inline-flex;
  align-items: end;

  ${plume.styles.heading.XXXS} {
    margin-right: 5px;
  }
`

const CommentWrapper = styled(styles.BackerCommentWrapper)``

const DateInfo = styled(plume.styles.heading.XXXXXXS)`
  color: ${plume.COLORS.TEXT_SECONDARY};
  text-transform: uppercase;
`
